import { CSSProperties } from 'react'
import Slider, { Range } from 'rc-slider'
import 'rc-slider/assets/index.css'
import Colors from 'modules/Colors'
import { clsx, styx } from 'modules/Utils'

interface Mark {
    label: string
    description: string
    placement?: 'first' | 'middle' | 'last'
    value: number
}

interface SliderSymphonyProps {
    /** Optional className for the container */
    className: string
    /** Optional style overrides */
    style?: CSSProperties
    /** Current value of the slider - can be single number or tuple for range */
    value: number | [number, number]
    /** Offset for the slider */
    sliderOffset: number
    /** Minimum value allowed */
    min: number
    /** Maximum value allowed */
    max: number
    /** Array of marks to display on the slider */
    marks: Mark[]
    /** Optional value mapping for non-linear sliders */
    valueMap?: Record<number, number>
    /** Callback when slider value changes */
    onChange: (value: number | [number, number]) => void
    /** Whether the slider is a range slider */
    isRange?: boolean
    /** Whether the slider is disabled */
    disabled?: boolean
    /** Whether to display marks equidistant regardless of their values */
    equidistant?: boolean
    /** Step value for snapping. If not provided, slider will slide smoothly */
    step?: number
    /** Whether to snap strictly to mark values only */
    snapToMarks?: boolean
}

const SliderSymphony = ({
    className = '',
    style = {},
    value,
    sliderOffset = 0,
    min,
    max,
    marks,
    valueMap = {},
    onChange,
    isRange = false,
    disabled = false,
    equidistant = false,
    step,
    snapToMarks = false
}: SliderSymphonyProps) => {
    /** Converts display value back to actual value if valueMap is provided */
    const reverseValueMap: Record<number, number> = Object.entries(valueMap).reduce((acc, [actual, display]) => ({
        ...acc,
        [display]: parseInt(actual)
    }), {})

    /** Static styles for the Symphony slider component */
    const sliderStyles = {
        /** Track styles for the slider */
        track: {
            backgroundColor: disabled ? Colors.grey : Colors.purpleLight,
            height: 24,
            borderRadius: 100,
        },
        /** Rail styles (the background track) */
        rail: {
            backgroundColor: disabled ? "#ddd" : '#e1d9eb',
            height: 24,
            borderRadius: 100,
        },
        /** Handle styles for the slider */
        handle: {
            borderColor: disabled ? Colors.greyDark : Colors.purple,
            backgroundColor: disabled ? Colors.greyDark : Colors.purple,
            opacity: 1,
            width: 34,
            height: 34,
            marginTop: -6,
            boxShadow: 'none'
        },
        /** Styles for the mark text below the slider */
        marks: {
            fontSize: 12,
            marginTop: 8,
            color: Colors.grey,
        },
        /** Styles for the mark dots on the slider */
        dot: {
            borderColor: disabled ? Colors.greyDark : Colors.purple,
            backgroundColor: disabled ? Colors.greyDark : Colors.purple,
            width: 8,
            height: 8,
            bottom: -12,
        }
    }

    /** Safely maps a value through the valueMap with type checking */
    const mapValue = (val: number): number => {
        return valueMap[val] !== undefined ? valueMap[val] : val
    }

    /** Create equidistant mark positions if equidistant is true */
    const getEquidistantMarkPositions = () => {
        if (!equidistant) {
            return marks
        }

        // Sort marks by their original values
        const sortedMarks = [...marks].sort((a, b) => a.value - b.value)

        // Create evenly spaced positions
        const spacing = (max - min) / (sortedMarks.length - 1)

        // Map to new positions while preserving other properties
        return sortedMarks.map((mark, index) => ({
            ...mark,
            value: min + (spacing * index)
        }))
    }

    /** Get the appropriate marks based on equidistant setting */
    const effectiveMarks = equidistant ? getEquidistantMarkPositions() : marks

    /** Map from equidistant position back to original value */
    const getOriginalValue = (equidistantValue: number): number => {
        if (!equidistant) {
            return equidistantValue
        }

        // Create mapping from equidistant positions to original values
        const sortedMarks = [...marks].sort((a, b) => a.value - b.value)
        const spacing = (max - min) / (sortedMarks.length - 1)

        // Find the closest equidistant position
        const index = Math.round((equidistantValue - min) / spacing)
        const boundedIndex = Math.max(0, Math.min(index, sortedMarks.length - 1))

        return sortedMarks[boundedIndex].value
    }

    /** Map from original value to equidistant position */
    const getEquidistantValue = (originalValue: number): number => {
        if (!equidistant) return originalValue

        // Create mapping from original values to equidistant positions
        const sortedMarks = [...marks].sort((a, b) => a.value - b.value)
        const spacing = (max - min) / (sortedMarks.length - 1)

        // Find the closest original value
        const closestMarkIndex = sortedMarks.reduce(
            (closest, mark, index) =>
                Math.abs(mark.value - originalValue) < Math.abs(sortedMarks[closest].value - originalValue)
                    ? index : closest,
            0
        )

        return min + (spacing * closestMarkIndex)
    }

    const renderMarks = (mark: Mark) => {
        const markStyle = { marginTop: 30 }

        let markPlacement: CSSProperties = { alignItems: 'center' }
        if (mark.placement === 'first') {
            markPlacement = { alignItems: 'flex-start', marginLeft: 80 }
        } else if (mark.placement === 'last') {
            markPlacement = { alignItems: 'flex-end', marginRight: 80 }
        }

        return {
            style: markStyle,
            label: (
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120, ...markPlacement }}>
                    <div>{mark.label}</div>
                    <div>{mark.description}</div>
                </div>
            )
        }
    }

    const formattedMarks = effectiveMarks.reduce((acc, mark) => ({
        ...acc,
        [valueMap[mark.value] || mark.value]: renderMarks(mark)
    }), {})

    /** Process slider change with equidistant handling */
    const handleChange = (newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            // Handle range values
            const processedValues = newValue.map(v => {
                const unmappedValue = reverseValueMap[v] || v
                return equidistant ? getOriginalValue(unmappedValue) : unmappedValue
            })
            onChange(processedValues as [number, number])
        } else {
            // Handle single value
            const unmappedValue = reverseValueMap[newValue] || newValue
            const processedValue = equidistant ? getOriginalValue(unmappedValue) : unmappedValue
            onChange(processedValue)
        }
    }

    /** Get the current value adjusted for equidistant display */
    const getCurrentValue = () => {
        if (isRange && Array.isArray(value)) {
            return [
                mapValue(equidistant ? getEquidistantValue(value[0]) : value[0]),
                mapValue(equidistant ? getEquidistantValue(value[1]) : value[1])
            ]
        } else if (!isRange && !Array.isArray(value)) {
            return mapValue(equidistant ? getEquidistantValue(value) : value)
        }
        // Default fallback
        return isRange ? [min, max] : min
    }

    return (
        <div
            className={clsx('sliderSymphonyElement', className)}
            style={styx(
                sty.sliderSymphonyElement,
                {
                    backgroundColor: disabled ? Colors.greyLighter : Colors.purpleLightest,
                },
                style
            )}
        >
            {isRange ?
                <Range
                    style={{ marginTop: -16 }}
                    min={min - sliderOffset}
                    max={max + sliderOffset}
                    marks={formattedMarks}
                    value={getCurrentValue() as [number, number]}
                    onChange={(newValue: number[]) => handleChange(newValue)}
                    trackStyle={[sliderStyles.track]}
                    railStyle={sliderStyles.rail}
                    handleStyle={[sliderStyles.handle, sliderStyles.handle]}
                    dotStyle={sliderStyles.dot}
                    allowCross={false}
                    pushable={true}
                    disabled={disabled}
                    step={snapToMarks ? null : step}
                    included={true}
                />
                :
                <Slider
                    style={{ marginTop: -16 }}
                    min={min - sliderOffset}
                    max={max + sliderOffset}
                    marks={formattedMarks}
                    value={getCurrentValue() as number}
                    onChange={(newValue) => handleChange(newValue)}
                    trackStyle={sliderStyles.track}
                    railStyle={sliderStyles.rail}
                    handleStyle={sliderStyles.handle}
                    dotStyle={sliderStyles.dot}
                    disabled={disabled}
                    step={snapToMarks ? null : step}
                    included={true}
                />
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    sliderSymphonyElement: {
        width: '100%',
        marginTop: 16,
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 14,
        paddingRight: 14,
        borderRadius: 100,
        marginBottom: 48,
    },
}

export default SliderSymphony